import React from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../layouts/MainLayout/constants";
import "./PageHeader.css";
import { useAppTranslation } from "../i18n/useAppTranslation";

type Props = {
  /** Rendered next to the back arrow. */
  title: React.ReactNode;
  /**
   * Where to go when there is nothing to go back to (deep link, refresh, or a
   * page opened straight from a map building). Defaults to the map.
   */
  fallbackTo?: string;
  /** Optional right-aligned slot — level badges, refresh, version switches. */
  extra?: React.ReactNode;
  className?: string;
};

/**
 * Title + back button for the inner pages that hang off MainLayout.
 *
 * Back prefers real history so it undoes whatever the user actually did, and
 * falls back to the map when there is none. react-router v6 stamps an `idx`
 * onto history.state, so `idx > 0` is a reliable "we have somewhere to go
 * back to inside this session" check — plain `history.length` is not, since it
 * counts entries from before the app was loaded.
 */
const PageHeader = ({ title, fallbackTo = ROUTES.HOME, extra, className }: Props) => {
  const navigate = useNavigate();
  const { t } = useAppTranslation("common");

  const goBack = () => {
    const idx = (window.history.state as any)?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate(fallbackTo);
  };

  return (
    <div className={"page-header" + (className ? ` ${className}` : "")}>
      <button
        type="button"
        className="page-header-back"
        onClick={goBack}
        aria-label={t("common:actions.back")}
      >
        <img src="/assets/images/icons/back.svg" alt="" />
      </button>
      <h1 className="page-header-title">{title}</h1>
      {extra ? <div className="page-header-extra">{extra}</div> : null}
    </div>
  );
};

export default PageHeader;
